import React, {useEffect, useRef} from 'react';
import {Animated, Easing, StyleSheet, View} from 'react-native';

// Loading skeleton for a club card — mirrors the design prototype's shimmer
// rows (Caissa Clubs.dc.html: skelRows / .cc-shimmer). RN can't animate CSS
// background-position, so each block draws a base tint with a highlight band
// that sweeps across it (translateX), reproducing the 1.3s shimmer.

const BASE = '#EFE9D8'; // shimmer base
const HIGHLIGHT = '#F8F3E4'; // shimmer highlight

function Shimmer({
  width,
  height,
  radius,
  progress,
}: {
  width: number | string;
  height: number;
  radius: number;
  progress: Animated.Value;
}) {
  return (
    <View
      style={{
        width: width as any,
        height,
        borderRadius: radius,
        backgroundColor: BASE,
        overflow: 'hidden',
      }}>
      <Animated.View
        style={{
          ...StyleSheet.absoluteFillObject,
          transform: [
            {
              translateX: progress.interpolate({
                inputRange: [0, 1],
                // sweep the highlight from left off-screen to right off-screen
                outputRange: [-200, 200],
              }),
            },
          ],
        }}>
        {/* A soft highlight band; three segments approximate the CSS gradient. */}
        <View style={{flex: 1, flexDirection: 'row'}}>
          <View style={{width: '40%'}} />
          <View
            style={{width: '20%', backgroundColor: HIGHLIGHT, opacity: 0.9}}
          />
          <View style={{width: '40%'}} />
        </View>
      </Animated.View>
    </View>
  );
}

export default function ClubCardSkeleton({
  count = 2,
  layout = 'list',
}: {
  count?: number;
  // 'list' = full-width stacked cards; 'grid' = flex-wrap, 3 cards per row (33%).
  layout?: 'list' | 'grid';
}) {
  const progress = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const loop = Animated.loop(
      Animated.timing(progress, {
        toValue: 1,
        duration: 1300,
        easing: Easing.inOut(Easing.ease),
        useNativeDriver: true,
      }),
    );
    loop.start();
    return () => loop.stop();
  }, [progress]);

  const card = (
    <View style={styles.card} testID="club-card-skeleton">
      <Shimmer width={52} height={52} radius={14} progress={progress} />
      <View style={{flex: 1, gap: 8}}>
        <Shimmer width="60%" height={14} radius={6} progress={progress} />
        <Shimmer width="40%" height={11} radius={6} progress={progress} />
        <Shimmer width="45%" height={18} radius={999} progress={progress} />
      </View>
    </View>
  );

  if (layout === 'grid') {
    return (
      <View style={styles.grid}>
        {Array.from({length: count}).map((_, i) => (
          <View key={i} style={styles.gridCell}>
            {card}
          </View>
        ))}
      </View>
    );
  }

  return (
    <View style={styles.list}>
      {Array.from({length: count}).map((_, i) => (
        <View key={i}>{card}</View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  // Stacked full-width cards.
  list: {
    width: '100%',
    gap: 12,
  },
  // Flex-wrap grid: 3 cards per row. Negative margin offsets cell padding.
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -9,
  },
  gridCell: {
    width: '33.33%',
    padding: 9,
  },
  card: {
    // fill the container (list row or grid cell) so the flex:1 text column and
    // its %-width shimmer lines size against the full card, not a collapsed row.
    width: '100%',
    alignSelf: 'stretch',
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    overflow: 'hidden',
    shadowColor: '#4D3F37',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
});
